import styled from '@emotion/styled';
import { css, keyframes } from '@emotion/react';
import { theme } from '@vfit/shared/themes';
import { ISelect } from './select.models';

const rotateEnter = keyframes`
  from {
    transform: rotate(0);
  }

  to {
    transform: rotate(-180deg);
  }
`;

const rotateExit = keyframes`
  from {
    transform: rotate(-180deg);
  }

  to {
    transform: rotate(0);
  }
`;

const rotateAnimation = css`
  .arrow-enter > svg {
    animation: ${rotateEnter} 0.3s forwards;
  }

  .arrow-exit > svg {
    animation: ${rotateExit} 0.3s forwards;
  }
`;

const optionsEnter = keyframes`
  from {
    opacity: 0;
    transform: scale3d(1, 0, 1);
    transform-origin: top center;
  }
  
  to {
    opacity: 1;
    transform: scale3d(1, 1, 1);
  }
`;

const optionsExit = keyframes`
  from {
    opacity: 1;
    transform: scale3d(1, 1, 1);
    transform-origin: top center;
  }
  
  to {
    opacity: 0;
    transform: scale3d(1, 0, 1);
  }
`;

const variants = {
  options: {
    animated: ({ error }: Pick<ISelect, 'error'>) => css`
      border-color: ${!error ? theme.colors.border.default : theme.colors.interactive.hover};
      border-radius: 0 0 ${theme.radius.sm} ${theme.radius.sm};
      border-style: solid;
      border-top: none;
      border-width: 1px;
      padding: 0;
      // padding: 5px 0 0;
      //top: ${theme.spacing['3xl']};
      top: 61px;
    `,
    'mva:animated': () => css`
      border-radius: ${theme.radius.md};
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      padding: 0;
      top: 61px;
    `,
    'mva:static': () => css`
      border-radius: ${theme.radius.md};
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
      padding: 0;
      top: 88px;
    `,
  },
  li: {
    animated: ({ 'aria-selected': selected }: { 'aria-selected'?: boolean }) => css`
      font-size: ${theme.typography.fontSize.body1};
      line-height: ${theme.typography.lineHeight.body1};
      font-weight: ${theme.typography.fontWeight.regular};
      color: ${!selected ? theme.colors.text.secondary : theme.colors.text.inverse};
      height: 40px;
      padding: ${theme.spacing.sm} ${theme.spacing.md} ${theme.spacing.sm} ${theme.spacing.lg};

      &:hover {
        background-color: ${theme.colors.bg.disabled};
        color: ${theme.colors.text.primary};
      }
    `,
    'mva:animated': ({ 'aria-selected': selected }: { 'aria-selected'?: boolean }) => css`
      font-size: ${theme.typography.fontSize.body2};
      line-height: ${theme.typography.lineHeight.body2};
      font-weight: ${theme.typography.fontWeight.regular};
      ${!selected ? '' : `font-weight:${theme.typography.fontWeight.bold}`};
      color: ${!selected ? theme.colors.text.primary : theme.colors.text.inverse};
      height: 50px;
      padding: ${theme.spacing.md};
      position: relative;

      &:hover {
        background-color: ${theme.colors.bg.secondary};
        color: ${theme.colors.text.primary};
        font-weight: ${theme.typography.fontWeight.bold};
      }

      &[aria-selected='false'] + li::after,
      &:hover + li::after {
        background-color: ${theme.colors.bg.disabled};
        top: 0;
        content: '';
        display: block;
        height: 1px;
        left: ${theme.spacing.md};
        position: absolute;
        width: calc(100% - ${theme.spacing.xl});
      }
    `,
    'mva:static': ({ 'aria-selected': selected }: { 'aria-selected'?: boolean }) => css`
      font-size: ${theme.typography.fontSize.body3};
      line-height: ${theme.typography.lineHeight.body3};
      font-weight: ${theme.typography.fontWeight.regular};
      ${!selected ? '' : `font-weight: ${theme.typography.fontWeight.bold}`};
      color: ${!selected ? theme.colors.text.primary : theme.colors.text.inverse};
      height: 50px;
      padding: ${theme.spacing.md};
      position: relative;

      &:hover {
        background-color: ${theme.colors.bg.secondary};
        color: ${theme.colors.text.primary};
        font-weight: ${theme.typography.fontWeight.bold};
      }

      &[aria-selected='false'] + li::after,
      &:hover + li::after {
        background-color: ${theme.colors.bg.disabled};
        top: 0;
        content: '';
        display: block;
        height: 1px;
        left: ${theme.spacing.md};
        position: absolute;
        width: calc(100% - ${theme.spacing.xl});
      }
    `,
  },
  child: {
    animated: () => css`
      font-size: ${theme.typography.fontSize.body1};
      line-height: ${theme.typography.lineHeight.body1};
      font-weight: ${theme.typography.fontWeight.regular};
      color: ${theme.colors.text.secondary};
    `,
    'mva:animated': ({ filteredItems }: { filteredItems: string[] }) => css`
      font-size: ${theme.typography.fontSize.body2};
      line-height: ${theme.typography.lineHeight.body2};
      font-weight: ${theme.typography.fontWeight.regular};
      color: ${theme.colors.text.primary};

      &::before {
        background-color: ${theme.colors.bg.disabled};
        content: '';
        display: ${filteredItems.length > 0 ? 'block' : 'none'};
        height: 1px;
        left: ${theme.spacing.md};
        position: absolute;
        width: calc(100% - ${theme.spacing.xl});
        top: -1px;
      }
    `,
    'mva:static': ({ filteredItems }: { filteredItems: string[] }) => css`
      font-size: ${theme.typography.fontSize.body2};
      line-height: ${theme.typography.lineHeight.body2};
      font-weight: ${theme.typography.fontWeight.regular};
      color: ${theme.colors.text.primary};

      &::before {
        background-color: ${theme.colors.bg.disabled};
        content: '';
        display: ${filteredItems.length > 0 ? 'block' : 'none'};
        height: 1px;
        left: ${theme.spacing.md};
        position: absolute;
        width: calc(100% - ${theme.spacing.xl});
        top: -1px;
      }
    `,
  },
};

export const Container = styled.div<Pick<ISelect, 'styled' | 'svg'>>`
  position: relative;
  ${rotateAnimation}
`;

interface OptionsProps {
  error?: string | boolean;
  variant?: string;
  maxHeight?: string;
  dropDownPosition?: 'relative' | 'absolute' | 'static' | 'unset';
}

export const Options = styled.ul<OptionsProps>`
  left: 0;
  list-style: none;
  overflow: ${({ maxHeight }) => (maxHeight ? 'auto' : 'hidden')};
  position: ${({ dropDownPosition }) => dropDownPosition ?? 'absolute'};
  transform-origin: top;
  transition: all 0.3s ease;
  width: calc(100% - 2px);
  z-index: 9;
  ${({ maxHeight }) => (maxHeight ? `max-height: ${maxHeight}` : '')};
  margin: ${({ dropDownPosition }) => (dropDownPosition === 'static' ? `-16px 0px 12px 0` : '0')};

  &.enter {
    animation: ${optionsEnter} 0.3s forwards;
  }

  &.exit {
    animation: ${optionsExit} 0.3s forwards;
  }

  > * {
    transition: all 0.3s ease;
  }

  > div {
    overflow-x: hidden !important;
  }

  > div::-webkit-scrollbar {
    width: ${theme.spacing.xs};
    height: ${theme.spacing.md};
    border-radius: ${theme.radius.lg};
  }

  > div::-webkit-scrollbar-thumb {
    background: ${theme.colors.bg.disabled};
    border-radius: ${theme.radius.lg};
  }

  > div::-webkit-scrollbar-track {
    background: ${theme.colors.bg.primary};
  }

  ${(props: any) => (props.variant ? (variants.options as any)[props.variant](props) : '')}
`;

interface LiProps {
  variant?: string;
  'aria-selected'?: boolean;
}

export const Li = styled.li<LiProps>`
  background-color: ${({ 'aria-selected': selected }) =>
    !selected ? theme.colors.bg.primary : theme.colors.bg.disabled};
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: all 0.3s ease;
  white-space: nowrap;
  ${(props: any) => (props.variant ? (variants.li as any)[props.variant](props) : '')}

  &:focus-visible {
    background-color: ${theme.colors.bg.disabled};
    outline: none;
  }
  &.selected {
    background: ${theme.colors.text.disabled};
  }
  ${(props: any) => (props.variant ? (variants.li as any)[props.variant](props) : '')}
`;

interface ChildProps {
  variant?: string;
  filteredItems: string[];
}

export const Child = styled.div<ChildProps>`
  background-color: ${theme.colors.bg.primary};
  padding: ${theme.spacing.md};
  position: relative;

  > a,
  > button {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  ${(props: any) => (props.variant ? (variants.child as any)[props.variant](props) : '')}
`;

